import type { ReactNode } from 'react';
import { ThemeToggle } from './ThemeToggle';
import { IS_NATIVE } from '../lib/base';
import { t } from '../lib/i18n';

/**
 * Layout para pantallas de acceso (login / registro): marca + toggle de tema
 * arriba y una tarjeta central con el contenido. Colores desde tokens.
 *
 * `aside` (opcional): panel lateral de marca. Cuando se pasa, el layout se
 * vuelve de dos columnas (form + panel) que se apilan en móvil. Sin `aside` se
 * mantiene la tarjeta centrada de siempre (registro / invitación).
 */
export function AuthLayout({
  children,
  wide = false,
  aside,
}: {
  children: ReactNode;
  wide?: boolean;
  aside?: ReactNode;
}) {
  return (
    <div className="relative flex min-h-full flex-col overflow-hidden bg-bg text-ink">
      {/* Profundidad de fondo (decorativa, aria-hidden). Mismo lenguaje que la
          landing: halo cálido de marca arriba + un segundo resplandor tenue
          abajo-izquierda + textura ñandutí muy sutil. Colores desde tokens. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute inset-x-0 top-0 h-80"
          style={{
            background:
              'radial-gradient(60% 100% at 50% 0%, color-mix(in srgb, var(--brand) 13%, transparent), transparent 70%)',
          }}
        />
        <div
          className="absolute -left-40 top-1/3 h-[420px] w-[420px] rounded-full blur-3xl"
          style={{
            background:
              'radial-gradient(circle, color-mix(in srgb, var(--brand) 10%, transparent), transparent 70%)',
          }}
        />
        <div className="landing-nanduti absolute -right-52 -top-40 h-[560px] w-[560px]" />
      </div>
      <header
        className="relative border-b border-line bg-surface/80 backdrop-blur"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4">
          {/* En nativo no hay landing: el logo es sólo marca (no navega a "/"). */}
          {IS_NATIVE ? (
            <span className="flex items-center gap-2">
              <img src={`${import.meta.env.BASE_URL}brand/logo-mark.png`} alt="" aria-hidden className="h-8 w-8" />
              <span className="font-display text-lg font-semibold text-ink">Tembiapo</span>
            </span>
          ) : (
            <a
              href={import.meta.env.BASE_URL}
              className="flex items-center gap-2 rounded-token focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <img src={`${import.meta.env.BASE_URL}brand/logo-mark.png`} alt="" aria-hidden className="h-8 w-8" />
              <span className="font-display text-lg font-semibold text-ink">Tembiapo</span>
            </a>
          )}
          <ThemeToggle />
        </div>
      </header>

      <main className="relative flex flex-1 flex-col items-center justify-center px-4 py-10">
        {/* Logo oficial en el hero (login nativo / registro / invitación, sin panel
            lateral). El mark es teal (se ve en claro y oscuro) y el wordmark usa
            text-ink → se adapta al tema. */}
        {!aside && (
          <div className="mb-8 flex flex-col items-center gap-2.5 text-center">
            <img
              src={`${import.meta.env.BASE_URL}brand/logo-mark.png`}
              alt="Tembiapo"
              className="h-16 w-16 drop-shadow-sm"
            />
            <span className="font-display text-3xl font-bold tracking-tight text-ink">Tembiapo</span>
          </div>
        )}
        {aside ? (
          <div className="grid w-full max-w-5xl items-stretch gap-6 lg:grid-cols-2 lg:gap-8">
            {/* Form primero en móvil (acceso rápido); a la derecha en desktop. */}
            <div className="order-1 flex flex-col justify-center lg:order-2">{children}</div>
            <div className="order-2 lg:order-1">{aside}</div>
          </div>
        ) : (
          <div className={wide ? 'w-full max-w-3xl' : 'w-full max-w-md'}>{children}</div>
        )}
        <p className="mt-6 text-center font-mono text-[11px] uppercase tracking-[0.18em] text-muted">
          {t('common.tagline')}
        </p>
        {/* Soporte + crédito Evoluado (regla de marca: presentes en toda superficie). */}
        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center text-xs text-muted">
          <a href={`${import.meta.env.BASE_URL}soporte`} className="transition-colors hover:text-brand-2">
            {t('common.support')}
          </a>
          <span aria-hidden className="text-line">·</span>
          <a
            href="https://evoluado.com"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-colors hover:text-brand-2"
          >
            {t('common.poweredBy')}
          </a>
        </div>
      </main>
    </div>
  );
}
